import {
  EnrichedAuditEvent,
  ActivityCode,
  EnrichedUser,
} from '@eppendorf/vnls-audit-event-client';
import { NotificationRule } from '@eppendorf/vnls-notifications-utils';
import { Badge, Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import { UserRole } from '@eppendorf/vnls-user-tenant-utils';
import { t } from 'i18next';

import { getDeviceEventCategoryLabel } from '$shared/i18n/device-event-category-label';
import { formatDateRange } from '$shared/utils/date.utils';

import { ObjectDiffView } from '$features/audit-events/components/diff-view/diff-view';
import { NotiRuleDiffDisplay } from '$features/audit-events/components/noti-rule-display/noti-rule-diff-display';
import { NotiRuleDisplay } from '$features/audit-events/components/noti-rule-display/noti-rule-display';
import {
  SentMessageDisplay,
  SentMessageMetadata,
} from '$features/audit-events/components/sent-message-display/sent-message-display';
import { SimpleDeviceDisplay } from '$features/audit-events/components/simple-device-display/simple-device-display';
import { UserListItem } from '$features/audit-events/components/user-list-item/user-list-item';
import { DeviceEventMetadata } from '$features/audit-events/types';
import { InfoItem } from '$features/devices/device-information/info-item';
import { InfoList } from '$features/devices/device-information/info-list';
import { roleDisplayNameMap } from '$features/shared/user-role-map';

import styles from './index.module.scss';

function hasName(entity: unknown): entity is { name: string } {
  return typeof entity === 'object' && entity !== null && 'name' in entity;
}

function hasId(entity: unknown): entity is { id: string } {
  return typeof entity === 'object' && entity !== null && 'id' in entity;
}

export function AuditEventMetadata({ event }: { event: EnrichedAuditEvent }) {
  switch (event.activityCode) {
    case ActivityCode.LOGIN:
    case ActivityCode.LOGOUT:
    case ActivityCode.INVITE_USER:
    case ActivityCode.START_PASSWORD_RESET:
    case ActivityCode.SUPPORT_ACCESS:
    case ActivityCode.SUPPORT_LEFT: {
      const originEntity = event.metadata?.originEntity as EnrichedUser;

      return (
        <InfoList.Content>
          <InfoItem
            label={t('auditEvents.detailsLabels.user')}
            value={
              <UserListItem
                name={
                  `${originEntity.firstName || ''} ${originEntity.lastName || ''}`.trim() ||
                  '-'
                }
                userRole={
                  originEntity.roles?.[0]
                    ? roleDisplayNameMap[originEntity.roles?.[0] as UserRole]
                    : '-'
                }
              />
            }
          />
        </InfoList.Content>
      );
    }
    case ActivityCode.DEVICE_EVENT_INFO:
    case ActivityCode.DEVICE_EVENT_WARNING:
    case ActivityCode.DEVICE_EVENT_ALERT:
    case ActivityCode.DEVICE_EVENT_ERROR: {
      const deviceEventEntity = event.metadata as DeviceEventMetadata;

      return (
        <InfoList.Content>
          <InfoItem
            label={t('auditEvents.detailsLabels.event')}
            value={
              deviceEventEntity?.deviceEvent.message ||
              t('auditEvents.detailsLabels.unknownEvent')
            }
          />
          <InfoItem
            label={t('auditEvents.detailsLabels.categories')}
            value={
              deviceEventEntity?.deviceEvent.categories?.length ? (
                <div className={styles.categoryList}>
                  {deviceEventEntity.deviceEvent.categories.map((categoryKey) => (
                    <Badge size="small" key={categoryKey}>
                      {getDeviceEventCategoryLabel(categoryKey, t)}
                    </Badge>
                  ))}
                </div>
              ) : (
                '-'
              )
            }
          />
        </InfoList.Content>
      );
    }
    case ActivityCode.ENABLE_ACCOUNT:
    case ActivityCode.USER_PROFILE_CHANGE:
    case ActivityCode.ROLE_CHANGED:
    case ActivityCode.PHONE_NUMBER_VERIFIED:
    case ActivityCode.NOTIFICATION_RULE_ARCHIVED:
    case ActivityCode.NOTIFICATION_RULE_TRIGGERED:
    case ActivityCode.INCIDENT_ACKNOWLEDGED:
    case ActivityCode.INCIDENT_RESOLVED:
    case ActivityCode.INCIDENT_EXPIRED:
    case ActivityCode.INCIDENT_ESCALATION_LAYER_1:
    case ActivityCode.INCIDENT_ESCALATION_LAYER_2:
    case ActivityCode.ORGANIZATION_SETTINGS_UPDATED:
    case ActivityCode.DEVICE_METADATA_CHANGED: {
      return <ObjectDiffView lhs={event.metadata?.before} rhs={event.metadata?.after} />;
    }
    case ActivityCode.NOTIFICATION_RULE_NOT_TRIGGERED_PAUSE: {
      const affectedRule = event.metadata?.affectedEntities?.at(0);

      const ruleName = hasName(affectedRule) ? affectedRule.name : t('shared.unknown');
      const ruleId = hasId(affectedRule) ? affectedRule.id : undefined;

      return (
        <InfoList.Content>
          <InfoItem
            label={t('shared.notificationRule')}
            value={
              <a
                className={styles.notiRuleLink}
                target="_blank"
                href={`/notifications/notifications-rules?sidecarRoute=notifications&id=${ruleId}`}
                rel="noreferrer"
              >
                <div className="flex flex__dir--row gap-s flex__ai--center">
                  {ruleName}{' '}
                  <Icon
                    name="external-link"
                    size={IconSizeClasses.XSmall}
                    className="bg-blue-500"
                  />
                </div>
              </a>
            }
          />
        </InfoList.Content>
      );
    }
    case ActivityCode.NOTIFICATION_RULE_UPDATED: {
      return (
        <>
          <InfoList.Content>
            <InfoItem
              label={t('shared.notificationRule')}
              value={
                <a
                  className={styles.notiRuleLink}
                  target="_blank"
                  href={`/notifications/notifications-rules?sidecarRoute=notifications&id=${(event.metadata?.after as NotificationRule)?.id}`}
                  rel="noreferrer"
                >
                  <div className="flex flex__dir--row gap-s flex__ai--center">
                    {(event.metadata?.after as NotificationRule)?.name}{' '}
                    <Icon
                      name="external-link"
                      size={IconSizeClasses.XSmall}
                      className="bg-blue-500"
                    />
                  </div>
                </a>
              }
            />
          </InfoList.Content>
          <NotiRuleDiffDisplay
            before={event.metadata?.before as NotificationRule | undefined}
            after={event.metadata?.after as NotificationRule | undefined}
          />
        </>
      );
    }
    case ActivityCode.DEVICE_DATA_EXPORT:
    case ActivityCode.START_DEVICE_REGISTRATION:
    case ActivityCode.START_DEVICE_RE_REGISTRATION:
    case ActivityCode.DEVICE_DEREGISTRATION:
    case ActivityCode.DEVICE_UPDATE_STARTED:
    case ActivityCode.DEVICE_UPDATE_SUCCEEDED:
    case ActivityCode.DEVICE_REGISTRATION_FINISHED: {
      return (
        <InfoList.Content>
          <InfoItem
            label={t('auditEvents.eventType.device')}
            value={<SimpleDeviceDisplay device={event.metadata?.affectedEntities} />}
          />
        </InfoList.Content>
      );
    }
    case ActivityCode.DEVICE_REGISTRATION_FAILED:
    case ActivityCode.DEVICE_DEREGISTRATION_FAILED:
    case ActivityCode.DEVICE_UPDATE_FAILED: {
      return (
        <InfoList.Content>
          <InfoItem
            label={t('shared.error')}
            value={(event.metadata?.error as string) ?? t('shared.unknownError')}
          />
          <InfoItem
            label={t('auditEvents.eventType.device')}
            value={<SimpleDeviceDisplay device={event.metadata?.affectedEntities} />}
          />
        </InfoList.Content>
      );
    }
    case ActivityCode.MESSAGE_SEND_OUT: {
      return (
        <SentMessageDisplay metadata={event.metadata as unknown as SentMessageMetadata} />
      );
    }
    case ActivityCode.NOTIFICATION_RULE_CREATED: {
      return <NotiRuleDisplay metadata={event.metadata?.after as NotificationRule} />;
    }
    case ActivityCode.STABILITY_REPORT_CREATED: {
      const metadataParameters: unknown = event.metadata?.parameters;
      const parameterNames = Array.isArray(metadataParameters)
        ? metadataParameters.flatMap((parameter: unknown) => {
            if (typeof parameter === 'string')
              return parameter.trim() ? [parameter.trim()] : [];
            if (typeof parameter !== 'object' || parameter === null) return [];
            const name =
              'name' in parameter && typeof parameter.name === 'string'
                ? parameter.name.trim()
                : '';
            const telemetryId =
              'telemetryId' in parameter && typeof parameter.telemetryId === 'string'
                ? parameter.telemetryId.trim()
                : '';
            return name || telemetryId ? [name || telemetryId] : [];
          })
        : [];
      return (
        <InfoList.Content>
          <InfoItem
            label="Report period"
            value={formatDateRange(
              event.metadata?.startDate as string,
              event.metadata?.endDate as string,
              true,
            )}
          />
          <InfoItem
            label={t('auditEvents.eventType.device')}
            value={<SimpleDeviceDisplay device={event.metadata?.affectedEntities} />}
          />
          {parameterNames.length > 0 && (
            <InfoItem
              label={t('monitoring.report.dialog.parameters')}
              value={parameterNames.join(', ')}
            />
          )}
        </InfoList.Content>
      );
    }
    case ActivityCode.AUDIT_TRAIL_EXPORT:
    case ActivityCode.REPORT_DOWNLOAD:
    case ActivityCode.REPORT_DELETED: {
      return (
        <InfoList.Content>
          <InfoItem
            label="Report period"
            value={formatDateRange(
              event.metadata?.startDate as string,
              event.metadata?.endDate as string,
            )}
          />
        </InfoList.Content>
      );
    }
    case ActivityCode.PHONE_NUMBER_VERIFICATION_FAILED: // event not implemented yet
    case ActivityCode.SUSPEND_ACCOUNT: // event not implemented yet
    case ActivityCode.UNSUSPEND_ACCOUNT: // event not implemented yet
    case ActivityCode.ARCHIVE_ACCOUNT: // event not implemented yet
    case ActivityCode.MAINTENANCE_SCHEDULED: // event not implemented yet
    case ActivityCode.MAINTENANCE_STARTED: // event not implemented yet
    case ActivityCode.MAINTENANCE_FINISHED: // event not implemented yet
    case ActivityCode.UNPLANNED_DOWNTIME_OCCURRED: // event not implemented yet
    case ActivityCode.TERMS_AND_CONDITIONS_UPDATED: // event not implemented yet
    case ActivityCode.PRIVACY_POLICY_UPDATED: // event not implemented yet
    case ActivityCode.SUBSCRIPTION_DETAILS_UPDATED: // event not implemented yet
    case ActivityCode.ORGANIZATION_DETAILS_UPDATED: // event not implemented yet
    default:
      return <pre>{JSON.stringify(event, null, 2)}</pre>;
  }
}
